/**
 * @fileoverview Module registry — the single catalog of module identities
 *               every gating decision references.
 * @module core-domain/modules/moduleRegistry
 *
 * Responsibilities:
 * - Declare the canonical module display names (MODULE_NAMES).
 * - Expose name-derived codes via the `MODULE` constant so no consumer ever
 *   writes a module-code literal by hand.
 * - Provide the shared gating helpers: `isModuleEnabled`,
 *   `moduleScopedPermission`, `MODULE_DISPLAY_NAMES`.
 *
 * Dependencies:
 * - ./moduleCode — the mechanical name -> code derivation.
 *
 * Architecture:
 * - `code` is the cross-environment module identity (permission strings,
 *   `?module=` filters, tenant `modules[]` entries, lookup `inspectionModules`
 *   metadata). All of those must key off the SAME value — this registry is
 *   that value.
 * - The system DB `modules` catalog remains the source of truth for whether
 *   a module exists/enabled; this registry is the code-side contract used
 *   for gating and scoped permission strings.
 * - `api/packages/core/src/modules/moduleRegistry.ts` mirrors this file for
 *   API-side consumers; the two are kept in parity by tests.
 *
 * Notes:
 * - Codes are generated, never literal — adding a module means adding a name
 *   to MODULE_NAMES; the code follows mechanically.
 * - Keep names aligned with the system-api seed catalog
 *   (api/system-api/src/db/seedData.ts DEFAULT_MODULE_CATALOG).
 */

import { moduleCodeFromName } from './moduleCode';

/**
 * @contract MODULE_NAMES
 * Purpose: Canonical display names for every platform module.
 * Fields:  key — registry identifier; value — the module's catalog `name`.
 * Rules:   the catalog `name` is the source of truth for the code; write
 *          paths normalize punctuation/whitespace/casing via
 *          `normalizeModuleName` before deriving.
 * Sync:    mirrored in api/system-api DEFAULT_MODULE_CATALOG — a name here
 *          must match the seeded name or the derived code diverges.
 */
export const MODULE_NAMES = {
  WILDLAND_FIRE: 'Wildland Fire',
  FIRE_CODE_COMPLIANCE: 'Fire Code Compliance',
  BUILDING_CODE_COMPLIANCE: 'Building Code Compliance',
  HYDRANT_MANAGEMENT: 'Hydrant Management',
  PERMITS_CERTIFICATES: 'Permits & Certificates',
  CUSTOMER_PORTAL: 'Customer Portal',
  CUSTOM_INSPECTION_PROGRAMS: 'Custom Inspection Programs',
  PUBLIC_EDUCATION: 'Public Education',
  COMMUNITY_RISK_REDUCTION: 'Community Risk Reduction',
  TCE_INTEGRATION: 'TCE Integration',
  BILLING: 'Billing',
  ADVANCED_AI: 'Advanced AI',
} as const;

export type ModuleRegistryKey = keyof typeof MODULE_NAMES;

function buildModuleCodes(): Record<ModuleRegistryKey, string> {
  const entries = Object.entries(MODULE_NAMES).map(([key, name]) => [
    key,
    moduleCodeFromName(name),
  ]);
  return Object.fromEntries(entries) as Record<ModuleRegistryKey, string>;
}

/**
 * The module-code constant: `MODULE.HYDRANT_MANAGEMENT === 'hydrantManagement'`.
 * Generated from MODULE_NAMES — codes cannot be hand-edited here.
 */
export const MODULE = buildModuleCodes();

/** Union of all valid module codes. */
export type ModuleCode = (typeof MODULE)[ModuleRegistryKey];

const ALL_MODULE_CODES = new Set<string>(Object.values(MODULE));

/** Whether `code` is a known platform module code. */
export function isKnownModuleCode(code: unknown): code is ModuleCode {
  return typeof code === 'string' && ALL_MODULE_CODES.has(code);
}

/** Reverse map: module code -> display name. */
export const MODULE_DISPLAY_NAMES: Record<ModuleCode, string> = Object.fromEntries(
  Object.entries(MODULE).map(([key, code]) => [code, MODULE_NAMES[key as ModuleRegistryKey]]),
) as Record<ModuleCode, string>;

/**
 * The single enabled-module gate. Consumers pass the tenant's enabled module
 * codes (from `extractEnabledModuleCodes`/`useTenantModules`/`hasModule`)
 * plus the registry code — never a raw literal.
 */
export function isModuleEnabled(enabledModules: readonly string[], code: ModuleCode): boolean {
  return enabledModules.includes(code);
}

/**
 * Builds a module-scoped permission string (`{module}.{permission}`),
 * e.g. `moduleScopedPermission(MODULE.FIRE_CODE_COMPLIANCE, 'inspections.view')`
 * -> `'fireCodeCompliance.inspections.view'`.
 */
export function moduleScopedPermission(code: ModuleCode, permission: string): string {
  return `${code}.${permission}`;
}
